import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Card, CardContent, CardHeader, CircularProgress, Grid, Snackbar, TextField, Typography } from "@mui/material";
import axios from "axios";
import { API_PATH } from "../../../App";
import { getAxiosConfig } from "../../../helpers/AuthHelper";

type SettingKey =
  | "monthlyBtcPriceIncreasePercent"
  | "monthlyDifficultyIncreasePercent"
  | "halvingDifficultyDecreasePercent"
  | "targetReturnPercent"
  | "uptimePercent";

const FIELDS: { key: SettingKey; label: string; helper: string }[] = [
  { key: "monthlyBtcPriceIncreasePercent", label: "Monthly BTC Price Increase (%)", helper: "Assumed BTC price growth per month" },
  { key: "monthlyDifficultyIncreasePercent", label: "Monthly Difficulty Increase (%)", helper: "Assumed network difficulty growth per month" },
  { key: "halvingDifficultyDecreasePercent", label: "Halving Month Difficulty Decrease (%)", helper: "One-time difficulty drop in each halving month, on top of the monthly increase (0 to under 100)" },
  { key: "targetReturnPercent", label: "Target Profit Percentage (%)", helper: "Total profit as % of machine price (0 to under 100)" },
  { key: "uptimePercent", label: "Uptime Percentage (%)", helper: "Assumed machine uptime" },
];

type Settings = Record<SettingKey, string>;

const EMPTY: Settings = {
  monthlyBtcPriceIncreasePercent: "",
  monthlyDifficultyIncreasePercent: "",
  halvingDifficultyDecreasePercent: "",
  targetReturnPercent: "",
  uptimePercent: "",
};

export default function PricePredictionSettings() {
  const [values, setValues] = useState<Settings>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });

  useEffect(() => {
    async function load() {
      try {
        const { data } = await axios.get(`${API_PATH}/price-prediction-settings`, getAxiosConfig());
        setValues(FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: String(data[f.key]) }), EMPTY));
      } catch (err: any) {
        setSnackbar({ open: true, message: "Failed to load settings", severity: "error" });
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  const save = async (regenerate: boolean) => {
    if (regenerate && !window.confirm("Save and regenerate today's prices? A new price batch will be created with these assumptions.")) {
      return;
    }
    setSaving(true);
    try {
      const body = FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: parseFloat(values[f.key]) }), {});
      await axios.put(`${API_PATH}/price-prediction-settings`, body, getAxiosConfig());
      if (regenerate) {
        await axios.get(`${API_PATH}/price-predictions/force-generate`, getAxiosConfig());
      }
      setSnackbar({
        open: true,
        message: regenerate ? "Settings saved and today's prices regenerated" : "Settings saved. They apply to the next generated price batch.",
        severity: "success",
      });
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.message || err?.message || "Failed to save settings", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}><CircularProgress /></Box>;
  }

  return (
    <div style={{ padding: "16px" }}>
      <Card elevation={4} sx={{ borderRadius: "16px" }}>
        <CardHeader
          title="Price Prediction Settings"
          subheader="Assumptions used by the daily price prediction and as the starting values of the Mining Hosting Price Calculator"
        />
        <CardContent>
          <Grid container spacing={2}>
            {FIELDS.map(f => (
              <Grid item xs={12} sm={6} md={4} key={f.key}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  name={f.key}
                  label={f.label}
                  helperText={f.helper}
                  value={values[f.key]}
                  onChange={handleChange}
                />
              </Grid>
            ))}
          </Grid>
          <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
            Prices are generated once a day, so saved changes apply to the next batch unless you regenerate now.
          </Typography>
          <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
            <Button variant="contained" onClick={() => save(false)} disabled={saving}>Save</Button>
            <Button variant="outlined" onClick={() => save(true)} disabled={saving}>Save &amp; Regenerate Today's Prices</Button>
          </Box>
        </CardContent>
      </Card>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </div>
  );
}
